/**
 * Owns the body-level backdrop lifecycle for every drawer direction.
 * Keeping the portal in one controller prevents side and bottom presentations
 * from developing separate interaction and animation rules.
 */
export default class SlidingContainerBackdrop {
    /**
     * @param {HTMLElement} host Element that supplies tokens, state, and event handlers.
     */
    constructor(host) {
        this.host = host;
        this.portalElement = null;
        this.animation = null;
    }

    /**
     * Returns whether an event target belongs to this backdrop.
     * @param {EventTarget|null} element Event target.
     * @returns {boolean} True for the shadow style source or body portal.
     */
    owns(element) {
        return element === this.host.backdropElement || element === this.portalElement;
    }

    /**
     * Returns the portal and optionally creates it.
     * @param {object} options Lookup options.
     * @param {boolean} options.create Create the portal when missing.
     * @returns {HTMLElement|null} Backdrop portal.
     */
    get({ create = false } = {}) {
        if (!this.host.backdrop) return null;
        return create ? this.ensure() : this.portalElement;
    }

    /**
     * Creates the single viewport backdrop used by every presentation.
     * @returns {HTMLElement|null} Backdrop portal.
     */
    ensure() {
        if (!this.host.backdrop) return null;

        if (!this.portalElement) {
            this.portalElement = document.createElement('div');
            this.portalElement.setAttribute('data-wje-sliding-container-backdrop', '');
            this.portalElement.setAttribute('aria-hidden', 'true');
            this.portalElement.classList.add(
                'sliding-container-backdrop',
                'sliding-container-backdrop-portal'
            );
            this.addListeners();
            document.body.append(this.portalElement);
        }

        this.syncStyles();
        return this.portalElement;
    }

    /**
     * Removes the portal and all listeners owned by it.
     */
    remove() {
        if (!this.portalElement) return;

        this.removeListeners();
        this.portalElement.remove();
        this.portalElement = null;
    }

    /**
     * Adds scroll blocking and optional dismiss interactions.
     */
    addListeners() {
        const backdrop = this.portalElement;
        if (!backdrop) return;

        backdrop.addEventListener('wheel', this.host.preventBackdropScroll, { passive: false });
        backdrop.addEventListener('touchmove', this.host.preventBackdropScroll, { passive: false });

        if (!this.host.backdropDismiss) return;

        backdrop.addEventListener('pointerdown', this.host.handleBackdropPointerStart);
        backdrop.addEventListener('pointermove', this.host.handleBackdropPointerMove);
        backdrop.addEventListener('pointerup', this.host.handleBackdropPointerEnd);
        backdrop.addEventListener('pointercancel', this.host.handleBackdropPointerCancel);
        backdrop.addEventListener('click', this.host.handleBackdropClick);
    }

    /**
     * Removes every listener that may have been attached to the portal.
     */
    removeListeners() {
        const backdrop = this.portalElement;
        if (!backdrop) return;

        backdrop.removeEventListener('wheel', this.host.preventBackdropScroll);
        backdrop.removeEventListener('touchmove', this.host.preventBackdropScroll);
        backdrop.removeEventListener('pointerdown', this.host.handleBackdropPointerStart);
        backdrop.removeEventListener('pointermove', this.host.handleBackdropPointerMove);
        backdrop.removeEventListener('pointerup', this.host.handleBackdropPointerEnd);
        backdrop.removeEventListener('pointercancel', this.host.handleBackdropPointerCancel);
        backdrop.removeEventListener('click', this.host.handleBackdropClick);
    }

    /**
     * Returns the active backdrop opacity token.
     * @returns {string} CSS opacity.
     */
    getOpenOpacity() {
        return getComputedStyle(this.host)
            .getPropertyValue('--wje-sliding-container-backdrop-opacity')
            .trim() || '1';
    }

    /**
     * Resolves the portal stacking layer.
     * @returns {string} CSS z-index.
     */
    getZIndex() {
        const hostStyle = getComputedStyle(this.host);
        const configuredZIndex = hostStyle
            .getPropertyValue('--wje-sliding-container-backdrop-z-index')
            .trim() || hostStyle
            .getPropertyValue('--wje-sliding-container-portal-backdrop-z-index')
            .trim();
        if (configuredZIndex) return configuredZIndex;

        const hostZIndex = Number.parseInt(hostStyle.zIndex, 10);
        return Number.isFinite(hostZIndex) ? String(hostZIndex - 1) : '999';
    }

    /**
     * Copies computed token styles from the shadow source to the body portal.
     */
    syncStyles() {
        if (!this.portalElement) return;

        const sourceStyle = this.host.backdropElement
            ? getComputedStyle(this.host.backdropElement)
            : null;
        const isOpen = this.host.classList.contains('open');

        Object.assign(this.portalElement.style, {
            position: 'fixed',
            inset: '0',
            display: 'block',
            opacity: isOpen ? this.getOpenOpacity() : '0',
            pointerEvents: isOpen ? 'auto' : 'none',
            touchAction: 'none',
            overscrollBehavior: 'none',
            background: sourceStyle?.background || 'rgba(0, 0, 0, .35)',
            backdropFilter: sourceStyle?.backdropFilter || 'none',
            webkitBackdropFilter: sourceStyle?.webkitBackdropFilter || 'none',
            zIndex: this.getZIndex(),
        });
    }

    /**
     * Cancels only the animation owned by this controller.
     * @param {Animation|null} animation Animation to cancel.
     */
    cancelAnimation(animation = this.animation) {
        if (!animation) return;

        animation.onfinish = null;
        animation.oncancel = null;
        animation.cancel();
        if (this.animation === animation) this.animation = null;
    }

    /**
     * Starts the shared backdrop transition.
     * @param {boolean} shouldOpen Target visibility.
     * @param {object} options Timing passed to the Web Animations API.
     * @returns {Animation|null} Started animation.
     */
    animate(shouldOpen, options) {
        this.cancelAnimation();

        const backdrop = this.get({
            create: shouldOpen || Boolean(this.portalElement),
        });
        if (!backdrop) return null;

        this.syncStyles();
        backdrop.style.pointerEvents = shouldOpen ? 'auto' : 'none';
        const openOpacity = this.getOpenOpacity();
        const animation = backdrop.animate(
            shouldOpen
                ? [{ opacity: '0' }, { opacity: openOpacity }]
                : [{ opacity: openOpacity }, { opacity: '0' }],
            options
        );

        this.animation = animation;
        return animation;
    }

    /**
     * Applies a settled open or closed state.
     * @param {boolean} isOpen Target visibility.
     */
    setOpen(isOpen) {
        if (!this.host.backdrop) {
            this.destroy();
            return;
        }

        const backdrop = this.get({
            create: isOpen || Boolean(this.portalElement),
        });
        if (!backdrop) return;

        this.syncStyles();
        backdrop.style.opacity = isOpen ? this.getOpenOpacity() : '0';
        backdrop.style.pointerEvents = isOpen ? 'auto' : 'none';

        if (!isOpen) this.remove();
    }

    /**
     * Cancels animation and removes all global state.
     */
    destroy() {
        this.cancelAnimation();
        this.remove();
    }
}
